import { Component, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { CheckboxModule } from 'primeng/checkbox';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LucideAngularModule } from 'lucide-angular';
import { SalesLeadData } from '../../models/discovery.models';
import { NotificationService } from '../../../core/services/notification.service';
import { PackLimitationsService } from '../../services/pack-limitations.service';
import { ContactModalService } from '../../services/contact-modal.service';
import { LimitedFeatures } from '../../models/pack.models';

@Component({
  selector: 'app-contact-sales-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    Textarea,
    CheckboxModule,
    TranslocoModule,
    LucideAngularModule,
  ],
  templateUrl: './contact-sales-modal.component.html',
  styleUrls: ['./contact-sales-modal.component.scss'],
})
export class ContactSalesModalComponent {
  protected packLimitationsService = inject(PackLimitationsService);
  protected contactModalService = inject(ContactModalService);
  private translocoService = inject(TranslocoService);
  private fb = inject(FormBuilder);
  private notificationService = inject(NotificationService);

  visible = false;
  isLoading = false;
  showSuccess = false;
  errorMessage = '';

  contactForm: FormGroup = this.fb.group({
    phone: [''],
    message: [''],
  });

  // Benefits de la version complète (clés pour accéder au tableau)
  readonly benefitsKeys: string[] = [];

  content = this.contactModalService.content;
  tripFeature = LimitedFeatures.Trip;
  createUser = LimitedFeatures.CreateUser;

  constructor() {
    effect(() => {
      this.visible = this.contactModalService.show();
      if (this.contactModalService.show()) {
        this.resetForm();
      }
    });
  }

  /**
   * Enables or disables all form fields
   */
  private toggleFormState(disabled: boolean): void {
    disabled ? this.contactForm.disable() : this.contactForm.enable();
  }

  /**
   * Submits the contact form
   */
  onSubmit(): void {
    if (this.contactForm.valid && !this.isLoading) {
      this.isLoading = true;
      this.toggleFormState(true);
      this.errorMessage = '';

      const formValue = this.contactForm.value;
      const salesLeadData: SalesLeadData = {
        phone: formValue.phone?.trim() || undefined,
        message: formValue.message?.trim() || undefined,
        content: this.content() ?? undefined,
        isDiscovery: this.packLimitationsService.isDiscovery(),
      };

      this.contactModalService.contactSales(salesLeadData).subscribe({
        next: () => {
          this.isLoading = false;
          this.toggleFormState(false);
          this.showSuccess = true;

          // Afficher la notification de succès
          this.notificationService.success(
            this.translocoService.translate(
              'discovery.contact.form.successMessage',
            ),
          );

          // Fermer la modale après 3 secondes
          setTimeout(() => {
            this.onClose();
          }, 3000);
        },
        error: (error) => {
          this.isLoading = false;
          this.toggleFormState(false);
          this.errorMessage = this.translocoService.translate(
            'discovery.contact.form.errorMessage',
          );
          console.error("Erreur lors de l'envoi du contact:", error);
        },
      });
    }
  }

  /**
   * Ferme la modale
   */
  onClose(): void {
    this.contactModalService.close();
    this.resetForm();
  }

  /**
   * Gère la fermeture via X ou Escape
   */
  onHide(): void {
    if (!this.isLoading) {
      this.onClose();
    }
  }

  /**
   * Remet à zéro le formulaire et les états
   */
  private resetForm(): void {
    this.contactForm.reset({
      phone: '',
      message: '',
    });
    this.showSuccess = false;
    this.errorMessage = '';
    this.isLoading = false;
  }

  /**
   * Récupère la liste des benefits depuis les traductions
   */
  get benefits(): string[] {
    return this.translocoService.translate('discovery.contact.benefits') || [];
  }
}
